// @greenlight-spec: 59-platform-agent.md#dashboard-ia
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { Link } from 'react-router-dom';

import { AgentPulse } from '@/components/agents/Transcript';
import { ListCard } from '@/components/greenlight/ListCard';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

import type { AgentDef, TriggerKind } from './fixtures';
import { AgentTile, runningSessions } from './shared';

const TRIGGER_WORD: Record<TriggerKind, string> = {
  human: 'On request',
  schedule: 'Scheduled',
  event: 'On event',
};

export function AgentCard({
  agent,
  enabled,
  onEnabledChange,
}: {
  agent: AgentDef;
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
}): React.JSX.Element {
  const running = enabled && runningSessions().some((s) => s.agentId === agent.id);
  const triggers = [...new Set(agent.triggers.map((t) => TRIGGER_WORD[t.kind]))].join(', ');

  return (
    <ListCard
      className={cn(
        'relative h-full transition-colors hover:border-[hsl(var(--border-strong))] [&>div]:h-full',
        !enabled && 'bg-background',
      )}
      data-testid={`agent-card-${agent.id}`}
    >
      <div className="flex h-full flex-col gap-3">
        <div className="flex items-start gap-3">
          <AgentTile agentId={agent.id} muted={!enabled} />
          <div className="min-w-0 flex-1">
            <Link
              to={`/agents/${agent.id}`}
              className="text-[15px] font-semibold tracking-tight after:absolute after:inset-0 after:rounded-[inherit] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring"
            >
              {agent.name}
            </Link>
            <div className="mt-0.5 flex items-center gap-1.5 text-[12px]">
              {running && <AgentPulse />}
              <span className={running ? 'font-medium text-indigo-ink' : 'text-muted-foreground'}>
                {!enabled ? 'Disabled' : running ? 'Running' : 'Idle'}
              </span>
            </div>
          </div>
          <span className="relative z-10 pt-0.5">
            <Switch
              checked={enabled}
              onCheckedChange={onEnabledChange}
              aria-label={`${agent.name} enabled`}
            />
          </span>
        </div>

        <p className="text-[13px] leading-relaxed">{agent.description}</p>

        <div className="mt-auto flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-muted-foreground">
          <span>
            {triggers} · {agent.scope} · {agent.health.sessions7d} sessions / 7d
          </span>
          {agent.gated.length > 0 && (
            <span className="whitespace-nowrap rounded-full bg-gl-amber/15 px-1.5 py-px text-[11px] font-medium text-gl-amber-text">
              Needs approval
            </span>
          )}
        </div>
      </div>
    </ListCard>
  );
}
